import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { barberService } from '../../services/barberService';
import { Barber } from '../../types/barber.types';
import { useToast } from '../../context/ToastContext';
import Modal from '../../components/common/Modal';
import AdminHeader from '../../components/admin/AdminHeader';

const ViewBarberPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [barber, setBarber] = useState<Barber | null>(null);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        loadBarber();
    }, [id]);

    const loadBarber = async () => {
        try {
            setLoading(true);
            const data = await barberService.getBarberById(Number(id));
            setBarber(data);
        } catch (error: any) {
            console.error('Error loading barber:', error);
            showToast('Error al cargar el barbero', 'error');
            navigate('/admin/barbers');
        } finally {
            setLoading(false);
        }
    };

    const buildImageUrl = (url?: string): string => {
        if (!url) return '';
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        return `/api${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const translateDayOfWeek = (day: string): string => {
        const translations: Record<string, string> = {
            'MONDAY': 'Lunes',
            'TUESDAY': 'Martes',
            'WEDNESDAY': 'Miércoles',
            'THURSDAY': 'Jueves',
            'FRIDAY': 'Viernes',
            'SATURDAY': 'Sábado',
            'SUNDAY': 'Domingo'
        };
        return translations[day] || day;
    };

    const handleDelete = () => {
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setShowDeleteModal(false);

        try {
            await barberService.deleteBarber(Number(id));
            showToast('Barbero eliminado correctamente', 'success');
            navigate('/admin/barbers');
        } catch (error: any) {
            console.error('Error deleting barber:', error);
            showToast(error.response?.data?.message || 'Error al eliminar el barbero', 'error');
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto p-xl">
                <div className="card p-xl text-center" style={{ background: '#111', border: '1px solid #2a2a2a', color: '#a3a3a3' }}>
                    <div className="spinner m-auto mb-md"></div>
                    <p>Cargando perfil...</p>
                </div>
            </div>
        );
    }

    if (!barber) {
        return (
            <div className="container mx-auto p-xl">
                <div className="card p-xl text-center" style={{ background: '#111', border: '1px solid #2a2a2a' }}>
                    <p style={{ color: '#ef4444' }}>No se pudo cargar el perfil del barbero</p>
                    <button className="btn mt-md" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#000', border: 'none', borderRadius: '0.5rem', fontWeight: '700' }} onClick={() => navigate('/admin/barbers')}>
                        Volver al listado
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <AdminHeader
                title="Perfil del Barbero"
                backPath="/admin/barbers"
                rightContent={
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="btn btn-sm"
                            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', color: '#000', borderRadius: '0.5rem', fontWeight: '700' }}
                            onClick={() => navigate(`/admin/barbers/${id}/edit`)}
                        >
                            ✏️ Editar
                        </button>
                        <button
                            className="btn btn-sm"
                            style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '0.5rem', fontWeight: '700' }}
                            onClick={handleDelete}
                        >
                            🗑️ Eliminar
                        </button>
                    </div>
                }
            />
            <div className="container mx-auto p-md md:p-xl" style={{ maxWidth: '1200px' }}>

                {/* Main Card */}
                <div className="card p-md md:p-xl" style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #2a2a2a', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                    {/* Profile Header */}
                    <div className="flex gap-lg mb-xl" style={{
                        paddingBottom: '2rem',
                        borderBottom: '1px solid #2a2a2a'
                    }}>
                        {/* Avatar */}
                        <div className="flex flex-col items-center">
                            <div
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    minWidth: '120px',
                                    minHeight: '120px',
                                    borderRadius: '50%',
                                    backgroundColor: barber.profilePicture ? 'transparent' : '#f59e0b',
                                    color: barber.profilePicture ? 'inherit' : '#000',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '3rem',
                                    overflow: 'hidden',
                                    border: '4px solid #f59e0b40',
                                    boxShadow: '0 0 25px rgba(245, 158, 11, 0.2)',
                                    marginBottom: '1rem'
                                }}>
                                {barber.profilePicture ? (
                                    <img
                                        src={buildImageUrl(barber.profilePicture)}
                                        alt={`${barber.firstName} ${barber.lastName}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: '50%'
                                        }}
                                    />
                                ) : (
                                    `${barber.firstName.charAt(0)}${barber.lastName.charAt(0)}`
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <span style={{
                                    fontSize: '0.7rem',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '1rem',
                                    fontWeight: '700',
                                    background: barber.isIndependent ? '#3b82f620' : '#8b5cf620',
                                    color: barber.isIndependent ? '#3b82f6' : '#8b5cf6',
                                    border: `1px solid ${barber.isIndependent ? '#3b82f640' : '#8b5cf640'}`
                                }}>
                                    {barber.isIndependent ? 'INDEPENDIENTE' : 'STAFF'}
                                </span>
                                <span style={{
                                    fontSize: '0.7rem',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '1rem',
                                    fontWeight: '700',
                                    background: barber.status === 'ACTIVE' ? '#22c55e20' : '#ef444420',
                                    color: barber.status === 'ACTIVE' ? '#22c55e' : '#ef4444',
                                    border: `1px solid ${barber.status === 'ACTIVE' ? '#22c55e40' : '#ef444440'}`
                                }}>
                                    {barber.status === 'ACTIVE' ? 'ACTIVO' : 'INACTIVO'}
                                </span>
                            </div>
                        </div>

                        {/* Basic Info */}
                        <div className="flex-1">
                            <p className="font-bold text-xl mb-xs" style={{ color: '#f59e0b' }}>"{barber.nickname}"</p>
                            <h2 className="font-bold mb-sm" style={{ fontSize: '1.75rem', margin: 0, color: '#e5e5e5' }}>
                                {barber.firstName} {barber.lastName}
                            </h2>
                            <p className="mb-md" style={{ color: '#737373' }}>{barber.email}</p>

                            <div className="flex gap-md flex-wrap">
                                {barber.whatsapp && (
                                    <div className="flex items-center gap-xs" style={{ color: '#a3a3a3' }}>
                                        <span style={{ fontSize: '1.25rem' }}>📱</span>
                                        <span>{barber.whatsapp}</span>
                                    </div>
                                )}
                                {barber.specialization && (
                                    <div className="flex items-center gap-xs" style={{ color: '#a3a3a3' }}>
                                        <span style={{ fontSize: '1.25rem' }}>✂️</span>
                                        <span>{barber.specialization}</span>
                                    </div>
                                )}
                                {barber.yearsOfExperience && (
                                    <div className="flex items-center gap-xs" style={{ color: '#a3a3a3' }}>
                                        <span style={{ fontSize: '1.25rem' }}>🏆</span>
                                        <span>{barber.yearsOfExperience} años de experiencia</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Details Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                        {/* Contact Information */}
                        <div>
                            <h3 className="font-bold mb-md" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', color: '#f59e0b' }}>
                                📞 Información de Contacto
                            </h3>
                            <div className="space-y-sm">
                                {barber.address && (
                                    <div className="p-md rounded-lg" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '0.75rem' }}>
                                        <p className="text-sm mb-xs" style={{ color: '#737373' }}>Dirección</p>
                                        <p className="font-semibold" style={{ color: '#e5e5e5' }}>{barber.address}</p>
                                    </div>
                                )}
                                {barber.whatsapp && (
                                    <div className="p-md rounded-lg" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '0.75rem' }}>
                                        <p className="text-sm mb-xs" style={{ color: '#737373' }}>WhatsApp</p>
                                        <p className="font-semibold" style={{ color: '#e5e5e5' }}>{barber.whatsapp}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Social Media */}
                        <div>
                            <h3 className="font-bold mb-md" style={{ fontSize: '1.25rem', color: '#f59e0b' }}>
                                🌐 Redes Sociales
                            </h3>
                            <div className="space-y-sm">
                                {barber.instagramUrl && (
                                    <div className="p-md rounded-lg" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '0.75rem' }}>
                                        <p className="text-sm mb-xs" style={{ color: '#737373' }}>Instagram</p>
                                        <a href={barber.instagramUrl} target="_blank" rel="noopener noreferrer"
                                            style={{ color: '#f59e0b', fontWeight: '600', textDecoration: 'none' }}>
                                            Ver perfil →
                                        </a>
                                    </div>
                                )}
                                {barber.facebookUrl && (
                                    <div className="p-md rounded-lg" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '0.75rem' }}>
                                        <p className="text-sm mb-xs" style={{ color: '#737373' }}>Facebook</p>
                                        <a href={barber.facebookUrl} target="_blank" rel="noopener noreferrer"
                                            style={{ color: '#f59e0b', fontWeight: '600', textDecoration: 'none' }}>
                                            Ver perfil →
                                        </a>
                                    </div>
                                )}
                                {!barber.instagramUrl && !barber.facebookUrl && (
                                    <p style={{ color: '#525252', fontStyle: 'italic' }}>No hay redes sociales configuradas</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Services */}
                    {barber.services && barber.services.length > 0 && (
                        <div className="mt-xl pt-xl" style={{ borderTop: '1px solid #2a2a2a' }}>
                            <h3 className="font-bold mb-md" style={{ fontSize: '1.25rem', color: '#f59e0b' }}>
                                💈 Servicios Ofrecidos
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                                {barber.services.map((service) => (
                                    <div key={service.id} className="p-md" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '0.75rem' }}>
                                        <p className="font-bold mb-xs" style={{ color: '#e5e5e5' }}>{service.name}</p>
                                        <p className="font-bold" style={{ color: '#f59e0b' }}>${service.price}</p>
                                        {service.isCustomPrice && (
                                            <p className="text-xs mt-xs" style={{ color: '#737373' }}>Precio personalizado</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Schedules */}
                    {barber.schedules && barber.schedules.length > 0 && (
                        <div className="mt-xl pt-xl" style={{ borderTop: '1px solid #2a2a2a' }}>
                            <h3 className="font-bold mb-md" style={{ fontSize: '1.25rem', color: '#f59e0b' }}>
                                📅 Horarios
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                                {barber.schedules.map((schedule) => (
                                    <div key={schedule.id} className="p-md" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '0.75rem' }}>
                                        <p className="font-bold mb-xs" style={{ color: '#e5e5e5' }}>{translateDayOfWeek(schedule.dayOfWeek)}</p>
                                        <p className="text-sm" style={{ color: '#a3a3a3' }}>
                                            {schedule.startTime} - {schedule.endTime}
                                        </p>
                                        {schedule.branchName && (
                                            <p className="text-xs mt-xs" style={{ color: '#737373' }}>📍 {schedule.branchName}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    title="Eliminar Barbero"
                >
                    <div style={{ marginBottom: '1.5rem' }}>
                        <p style={{ margin: 0, marginBottom: '1rem', color: '#e5e5e5' }}>
                            ¿Desea desactivar el barbero <strong style={{ color: '#f59e0b' }}>{barber?.firstName} {barber?.lastName}</strong>?
                        </p>
                        <p style={{ margin: 0, color: '#737373', fontSize: '0.9rem' }}>
                            El barbero será desactivado y no aparecerá en el sistema. Podrá ser reactivado en el futuro.
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button
                            className="btn"
                            style={{ background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.5rem' }}
                            onClick={() => setShowDeleteModal(false)}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn"
                            style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: '0.5rem', fontWeight: '700' }}
                            onClick={confirmDelete}
                        >
                            Eliminar
                        </button>
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default ViewBarberPage;
